import ky from "ky";
import { RelodeApiResponse, ValidationError } from ".";
import { RelodeErrorBase } from "./relodeErrorBase";

type ApiErrorType =
    | "client"
    | "server"
    | "timeout"
    | "abort"
    | "unknown"
    | "typeError";

export class RelodeApiError extends RelodeErrorBase {
    public validationErrors: ValidationError[];
    public summary: string;
    public status: number;
    public statusText: string;
    public trackingData: { name: string; data?: string | object };
    public backendStackTrace: string[] = [];
    public url: string;
    public type: ApiErrorType = "unknown";
    public friendlyMessages: string;
    public requestId: string;
    public requestBody: string | object;
    public isAbort = (): boolean => this.type === "abort";

    /**
     * @description 400 Http status code
     */
    public isBadRequest = (): boolean => this.status === 400;

    /**
     * @description 404 Http status code
     */
    public isNotFound = (): boolean => this.status === 404;

    /**
     * @description 409 Http status code
     */

    public isConflict = (): boolean => this.status === 409;
    /**
     * @description 403 Http status code
     */

    public isForbidden = (): boolean => this.status === 403;
    /**
     * @description 412 Http status code
     */

    public isPreconditionFailed = (): boolean => this.status === 412;
    /**
     * @description 400 - 499 Http status code
     */

    public isClientError = (): boolean =>
        this.status >= 400 && this.status <= 499;
    /**
     * @description 500 - 599 Http status code
     */

    public isServerError = (): boolean =>
        this.status >= 500 && this.status <= 599;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected constructor(message: string) {
        super("RelodeApiError: " + message);
    }

    getTelemetryData = () => {
        const data = {
            url: this.url,
            status: this.status.toString(),
            stack: this.stack,
            type: this.type,
            requestId: this.requestId,
            origin: this.origin,
        };

        const _validationErrors = createValidationErrorObject(
            this.validationErrors
        );

        if (_validationErrors) {
            data["validationErrors"] = _validationErrors;
        }
        if (!!this.summary) data["summary"] = this.summary;
        if (!!this.trackingData) data["trackingData"] = this.trackingData;
        if (!!this.requestBody) data["requestBody"] = this.requestBody;
        if (!!this.backendStackTrace)
            data["backendStackTrace"] = this.backendStackTrace;
        return data;
    };

    private static fromHttpError = async (
        error: InstanceType<typeof ky.HTTPError>,
        trackingData?: { name: string; data?: string | object }
    ): Promise<RelodeApiError> => {
        let relodeResponse: RelodeApiResponse<any>;
        try {
            relodeResponse = await error.response.json();
        } catch (err) {
            relodeResponse = {
                ok: false,
                result: undefined,
                stackTrace: [error.stack],
                status: error.response.status,
            };
        }

        if (!relodeResponse) {
            relodeResponse = {
                ok: false,
                result: undefined,
                stackTrace: [
                    "no stacktrace is available as the http fetch did not return a useable response",
                ],
                status: error.response.status,
            };
        }

        const relodeMessages = getRelodeMessages(relodeResponse);
        const typeCode = Math.floor(error.response.status / 100);
        const result: RelodeApiError = new RelodeApiError(error.message);
        result.name = error.name;
        result.stack = error.stack;
        result.friendlyMessages = createFriendlyMessages(relodeMessages);
        result.status = error.response.status;
        result.statusText = error.response.statusText;
        result.url = error.response.url;
        result.summary = relodeMessages.summary;
        result.validationErrors = relodeMessages.validationErrors;
        result.backendStackTrace = relodeResponse.stackTrace;
        result.trackingData = trackingData;
        result.type =
            typeCode === 4 ? "client" : typeCode === 5 ? "server" : "unknown";
        return result;
    };

    private static fromUnknown = (error: any) => {
        const result = new RelodeApiError(error.message);
        result.name = error.name;
        result.stack = error.stack;
        result.statusText = error.name || "Unknown";
        result.status = -1;
        result.type = getUnknownType(error);
        return result;
    };

    static create = async (
        error: InstanceType<typeof ky.HTTPError> | any,
        url: string,
        requestId: string,
        body?: any,
        trackingData?: { name: string; data?: string | object }
    ): Promise<RelodeApiError> => {
        let apiError: RelodeApiError;
        if (error instanceof ky.HTTPError) {
            apiError = await RelodeApiError.fromHttpError(error);
        } else {
            apiError = RelodeApiError.fromUnknown(error);
        }

        const bodyString =
            typeof body === "object"
                ? JSON.stringify(body, null, 4)
                : body === undefined || body === null
                ? "no body"
                : body;

        apiError.requestId = requestId;
        apiError.trackingData = trackingData;
        apiError.requestBody = bodyString;
        apiError.url = apiError.url || url;
        return apiError;
    };
}

const createValidationErrorObject = (errors: ValidationError[]) => {
    if (!errors || (errors?.filter(e => !!e)?.length ?? 0) < 1)
        return undefined;

    const result = {};
    errors
        .filter(e => !!e)
        .map(e => {
            result[e.field] = e.messages;
        });

    return result;
};
const createValidationErrorString = (errors: ValidationError[]) => {
    if (!errors) return undefined;

    const errs = errors.filter(e => !!e);
    if (errs.length === 0) return undefined;
    const builder = new StringBuilder();

    errs.forEach(e =>
        builder.appendLine(`${e.field}: ${e.messages.join(" | ")}`)
    );

    return builder.build();
};

class StringBuilder {
    private _string: string;
    public append = (str: string) => {
        this._string = this._string + str;
        return this;
    };

    public appendLine = (str: string) => {
        this._string = "\n" + this._string + "\n";
        return this;
    };

    public build = () => {
        return this._string;
    };
}
const createFriendlyMessages = (
    messages: ReturnType<typeof getRelodeMessages>
) => {
    const { summary } = messages;

    const validations = messages.validationErrors
        .map(v => `${v.messages.join("\n")}`)
        .join("\n");
    const result = [summary, validations].join("\n");
    return result.trim();
};

const getUnknownType = (error: any): ApiErrorType => {
    if (error.name === undefined) {
        return "unknown";
    }

    if (error instanceof Error) {
        if (
            error.name.toLowerCase().indexOf("abort") >= 0 ||
            error.message.toLowerCase().indexOf("abort") >= 0
        ) {
            return "abort";
        }

        switch (error.name) {
            case "TimeoutError": {
                return "timeout";
            }

            case "AbortError": {
                return "abort";
            }

            case "TypeError": {
                return "typeError";
            }

            default: {
                return "unknown";
            }
        }
    }
    return "unknown";
};

const getRelodeMessages = (response: any) => {
    const errors = response.errors || {};
    const { summary } = errors;

    const validationErrors = Object.keys(errors)
        .filter(k => k !== "summary")
        .map(k => ({ field: k, messages: errors[k] }));
    return { summary: summary?.join(" | "), validationErrors };
};
